import { Plus } from 'lucide-react'
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview'
import { pointerOutsideOfPreview } from '@atlaskit/pragmatic-drag-and-drop/element/pointer-outside-of-preview'
import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'

/** Row look shared by the space sidebar, matching the nav rows (design 6a / 21 C). */
export const ROW_BASE =
  'group/row relative flex h-8 w-full min-w-0 items-center gap-2 rounded-md px-2.5 text-left text-sm transition-colors'
export const ROW_IDLE = 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
export const ROW_ACTIVE =
  'bg-sidebar-active text-sidebar-active-foreground shadow-[0_0_0_1px_var(--sidebar-border),var(--shadow-2xs)]'

export function rowClass(active: boolean, className?: string) {
  return cn(ROW_BASE, active ? ROW_ACTIVE : ROW_IDLE, className)
}

/** Quiet count on the right of a row. */
export function RowCount({ children }: { children: ReactNode }) {
  return <span className="ml-auto shrink-0 text-xs tabular-nums text-muted-foreground">{children}</span>
}

/**
 * Section label: 10.5px caps with .1em spacing (design 24a) and a "+" on the
 * right that only shows on hover or keyboard focus (design 22c).
 */
export function SectionHeader({
  label,
  onAdd,
  addLabel,
  className,
}: {
  label: string
  onAdd?: () => void
  addLabel?: string
  className?: string
}) {
  return (
    <div className={cn('group/section flex h-7 items-center gap-2 pl-[18px] pr-3', className)}>
      <h3 className="shrink-0 text-[10.5px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">{label}</h3>
      {onAdd && (
        <button
          type="button"
          aria-label={addLabel ?? label}
          title={addLabel}
          onClick={onAdd}
          className={cn(
            'ml-auto flex size-5 shrink-0 items-center justify-center rounded text-muted-foreground transition hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
            'opacity-0 focus-visible:opacity-100 group-hover/section:opacity-100 [@media(hover:none)]:opacity-100',
          )}
        >
          <Plus className="size-3.5" />
        </button>
      )}
    </div>
  )
}

/**
 * Where a dragged row will land (design 22c): a terracotta line with a small
 * ring at its left, indented to the level it lands at.
 */
export function DropLine({ edge, indentPx = 8 }: { edge: 'top' | 'bottom'; indentPx?: number }) {
  return (
    <span
      aria-hidden
      data-testid="sidebar-drop-line"
      className={cn(
        'pointer-events-none absolute right-2 z-20 flex h-2 items-center',
        edge === 'top' ? '-top-1' : '-bottom-1',
      )}
      style={{ left: indentPx }}
    >
      <span className="size-2 shrink-0 rounded-full border-2 border-brand bg-sidebar" />
      <span className="h-0.5 flex-1 rounded-full bg-brand" />
    </span>
  )
}

/**
 * The lifted copy of a sidebar row: a card with a shadow holding the row's
 * icon and name. Rendered by the browser as the native drag image.
 */
export function sidebarDragPreview(
  nativeSetDragImage: Parameters<typeof setCustomNativeDragPreview>[0]['nativeSetDragImage'],
  row: HTMLElement,
  label: string,
) {
  setCustomNativeDragPreview({
    nativeSetDragImage,
    getOffset: pointerOutsideOfPreview({ x: '12px', y: '8px' }),
    render: ({ container }) => {
      const card = document.createElement('div')
      card.className =
        'flex w-[220px] items-center gap-2 rounded-lg border border-border bg-card px-3 py-1.5 text-sm text-card-foreground shadow-lg'
      const icon = row.querySelector('[data-drag-icon]')
      if (icon) card.append(icon.cloneNode(true))
      const title = document.createElement('span')
      title.className = 'min-w-0 flex-1 truncate'
      title.textContent = label
      card.append(title)
      container.append(card)
    },
  })
}
